import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatFCFA, statusLabel, deliveryLabel } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Package, MapPin, User, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import Reveal from "@/components/Reveal";

export default function Account() {
  const navigate = useNavigate();
  const { user, logout, isCustomer, loading } = useAuth();
  const { t, lang } = useI18n();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!isCustomer) {
      navigate("/connexion", { state: { next: "/compte" } });
      return;
    }
    setFetching(true);
    api.get(`/orders/customer/${encodeURIComponent(user.email)}`)
      .then((r) => setOrders(r.data))
      .finally(() => setFetching(false));
  }, [user, isCustomer, loading, navigate]);

  if (!isCustomer) return null;

  const totalSpent = orders.reduce((s, o) => s + o.total, 0);

  return (
    <div className="bg-white text-[var(--dsf-text)] min-h-screen">
      <Header />
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-20" data-testid="account-page">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <Reveal>
            <div className="flex flex-wrap justify-between items-end gap-4 mb-12">
              <div>
                <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">{t("nav.account")}</div>
                <h1 className="font-serif text-5xl md:text-6xl text-[var(--dd-green)]">{lang === "fr" ? `Bonjour, ${user.name.split(" ")[0]}` : `Hello, ${user.name.split(" ")[0]}`}</h1>
              </div>
              <button onClick={() => { logout(); navigate("/"); }} className="btn-outline-dark inline-flex" data-testid="account-logout">
                <LogOut size={14} /> {t("nav.logout")}
              </button>
            </div>
          </Reveal>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[
              { label: lang === "fr" ? "Commandes" : "Orders", value: orders.length },
              { label: lang === "fr" ? "Total dépensé" : "Total spent", value: formatFCFA(totalSpent) },
              { label: lang === "fr" ? "Membre depuis" : "Member since", value: "2026" },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="card-light p-6">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--dsf-text-muted)]">{s.label}</div>
                  <div className="font-serif text-3xl font-semibold text-[var(--dd-green)] mt-2">{s.value}</div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Orders */}
          <h2 className="font-serif text-3xl text-[var(--dd-green)] mb-6">{lang === "fr" ? "Mes commandes" : "My orders"}</h2>

          {fetching && <div className="text-center text-[var(--dsf-text-muted)] py-12">{t("common.loading")}</div>}

          {!fetching && orders.length === 0 && (
            <div className="card-light p-12 text-center" data-testid="account-no-orders">
              <Package size={32} className="text-[var(--dsf-text-muted)] mx-auto mb-3" />
              <p className="text-[var(--dsf-text-secondary)] mb-4">{lang === "fr" ? "Aucune commande pour le moment." : "No orders yet."}</p>
              <Link to="/" className="btn-primary inline-flex">{lang === "fr" ? "Découvrir nos plats" : "Discover our dishes"}</Link>
            </div>
          )}

          {!fetching && orders.length > 0 && (
            <div className="space-y-4">
              {orders.map((o, i) => (
                <Reveal key={o.id} delay={i * 50}>
                  <Link
                    to={`/confirmation/${o.id}`}
                    className="block card-light p-6 hover:border-[var(--dsf-green)] transition-colors"
                    data-testid={`order-row-${o.order_number}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <div className="font-serif text-xl font-semibold text-[var(--dd-green)]">{o.order_number}</div>
                        <div className="text-xs text-[var(--dsf-text-muted)] mt-1">
                          {new Date(o.created_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", { day: "numeric", month: "long", year: "numeric" })}
                          {" · "}{deliveryLabel(o.delivery_method)}
                        </div>
                      </div>
                      <span className={`status-pill status-${o.status}`}>{statusLabel(o.status)}</span>
                      <div className="font-serif text-xl font-semibold">{formatFCFA(o.total)}</div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
